import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useDispatch, useStore } from 'react-redux';
import $ from 'jquery';
import camelCase from 'lodash.camelcase';
import merge from 'lodash.merge';

import styled from 'styled-components';
import moment, { Moment } from 'moment';
import { mergeStyleWithState } from '../../utils/mergeStyle';
import { history } from '../../configureStore';
import { selectVariableData, selectLocalVariableData } from '../../store/page/selectors';
import ContainerSkeleton from '../skeleton/ContainerSkeleton';
import { formValuesRequest, submitFormRequest, mergeStyleRequest, pageRequest, updateVariableSuccess, actionRequest } from '../../store/page/actions';
import { combineClassNames, replacePlaceholdersWithVariablesV2, handleOpenUrl, getSizesBasedOnScreen, runFunctionsWithTiming, getNestedValue, parseIfJSON, isClickInsidePortal, } from '../../utils/base';
import { buildInternalFns } from '../../utils/handlerScope';
import { buildVariablesData, isVariablesChunk } from '../../utils/variableChunk';
import 'daterangepicker';
import 'daterangepicker/daterangepicker.css';
import {
  DateTimePickerInput,
  StyleUpdateChunk, RequestUpdateChunk, FunctionUpdateChunk,
  Breakpoints,
  ActionChunk,
} from '../../types/base';

import generateComponentStyles from '../../utils/styleHelper';
import { useInitEntrance } from '../../utils/useInitEntrance';
import * as styles from './DateRangePicker.module.scss';
import './DateRangePicker.scss';

type MaxSpanObj = {
    days?: number | string;
}

const ranges = (filterMinDate?: string, filterMaxDate?: string, maxSpan?: MaxSpanObj) => {
  const minDate = filterMinDate ? moment(filterMinDate) : null;
  const maxDate = filterMaxDate ? moment(filterMaxDate) : null;
  const maxSpanDays = maxSpan?.days ? parseInt(maxSpan.days as string, 10) : null;

  const applyMaxSpan = (start: Moment, end: Moment) => {
    if (maxSpanDays) {
      const maxStartDate = end.clone().subtract(maxSpanDays, 'days');
      start = start.isBefore(maxStartDate) ? maxStartDate : start;
    }
    if (maxDate && end.isAfter(maxDate)) {
      end = maxDate;
    }
    return [start, end];
  };

  return {
    'Сьогодні': applyMaxSpan(minDate && moment().startOf('day').isBefore(minDate) ? minDate : moment().startOf('day'), moment().endOf('day')),
    'Вчора': applyMaxSpan(minDate && moment().subtract(1, 'day').startOf('day').isBefore(minDate) ? minDate : moment().subtract(1, 'day').startOf('day'), moment().subtract(1, 'day').endOf('day')),
    'Поточний тиждень': applyMaxSpan(minDate && moment().startOf('isoWeek').isBefore(minDate) ? minDate : moment().startOf('isoWeek'), moment().endOf('isoWeek')),
    'Минулий тиждень': applyMaxSpan(minDate && moment().subtract(1, 'week').startOf('isoWeek').isBefore(minDate) ? minDate : moment().subtract(1, 'week').startOf('isoWeek'), moment().subtract(1, 'week').endOf('isoWeek')),
    'Поточний місяць': applyMaxSpan(minDate && moment().startOf('month').isBefore(minDate) ? minDate : moment().startOf('month'), moment().endOf('month')),
    'Минулий місяць': applyMaxSpan(minDate && moment().subtract(1, 'month').startOf('month').isBefore(minDate) ? minDate : moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')),
    'Поточний рік': applyMaxSpan(minDate && moment().startOf('year').isBefore(minDate) ? minDate : moment().startOf('year'), moment().endOf('year')),
    'Минулий рік': applyMaxSpan(minDate && moment().subtract(1, 'year').startOf('year').isBefore(minDate) ? minDate : moment().subtract(1, 'year').startOf('year'), moment().subtract(1, 'year').endOf('year')),
    'Весь час': applyMaxSpan(minDate && moment().startOf('year').isBefore(minDate) ? minDate : moment().startOf('year'), moment().endOf('year')),
  };
};
const preprocess = (obj: any): any => {
  obj = typeof obj === 'string' ? parseIfJSON(obj) : obj;

  if (obj && typeof obj === 'object') {
    if (typeof obj.locale === 'string') {
      obj.locale = parseIfJSON(obj.locale);
    }
    if (obj.locale && typeof obj.locale.days_of_week === 'string') {
      obj.locale.days_of_week = parseIfJSON(obj.locale.days_of_week);
    }
    if (obj.locale && typeof obj.locale.month_names === 'string') {
      obj.locale.month_names = parseIfJSON(obj.locale.month_names);
    }
  }
  return obj;
};

const transformKeysDeep = (obj: any): any => {
  obj = preprocess(obj);

  if (Array.isArray(obj)) {
    return obj.map(item => transformKeysDeep(item));
  }

  if (obj && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const value = obj[key];
      const newKey = camelCase(key);
      acc[newKey] = transformKeysDeep(value);
      return acc;
    }, {});
  }

  return obj;
};

const colorVarMap: Record<string, string> = {
  applyButtonBg: '--drp-apply-bg',
  applyButtonBorder: '--drp-apply-border',
  applyButtonText: '--drp-apply-text',
  applyButtonBgDisabled: '--drp-apply-bg-disabled',
  applyButtonBorderDisabled: '--drp-apply-border-disabled',
  applyButtonTextDisabled: '--drp-apply-text-disabled',
  cancelButtonBg: '--drp-cancel-bg',
  cancelButtonBorder: '--drp-cancel-border',
  cancelButtonText: '--drp-cancel-text',
  rangeActiveBg: '--drp-range-active-bg',
  rangeActiveText: '--drp-range-active-text',
  rangeHoverBg: '--drp-range-hover-bg',
};

const applyDrpColors = (container: HTMLElement | null | undefined, colors: Record<string, string> | undefined) => {
  if (!container || !colors || typeof colors !== 'object') return;
  Object.keys(colors).forEach((key) => {
    const cssVar = colorVarMap[key];
    const value = colors[key];
    if (cssVar && typeof value === 'string' && value) {
      container.style.setProperty(cssVar, value);
    }
  });
};

const StyledInput = styled.input<{
  $shape?: any;
  $baseScreenResolution?: number;
  $skipStates?: boolean;
  $parentDisplay?: string;
  $parentFlexDirection?: string;
}>`
    z-index: 1;
  ${(props) => generateComponentStyles(props.$shape, props.$baseScreenResolution, props.$skipStates, props.$parentDisplay, undefined, props.$parentFlexDirection)}
`;

const validateInputPattern = (value: string, pattern?: string): boolean => {
  if (!pattern) return true;
  try {
    const regex = new RegExp(pattern);
    return regex.test(value);
  } catch (e) {
    console.error('Invalid regex pattern:', e);
    return true; // Return true if pattern is invalid to avoid blocking input
  }
};

interface Ranges {
  [key: string]: [Moment, Moment];
}

interface DateTimePickerInputType extends DateTimePickerInput {
  dataLake?: any;
  nestedTable?: boolean;
  parentIndex?: number;
  styleChunks?: any;
  pageSettings?: any;
  parentRef?: any;
  parentLoading?: boolean;
  parentStyle?: any;
  enableLocalShimmer?: boolean;
  valueOriginal?: string;
  cache?: boolean;
}

const getTimeRanges = (inputValue?: string, ranges?: Ranges, separator = ' : ', format = 'HH:mm DD/MM/YY') => {
  let start: Moment | undefined;
  let end: Moment | undefined;

  if (!inputValue) {
    return { start, end };
  }

  const separatorRegex = new RegExp(`\\s*${separator.trim()}\\s*`);
  if (separatorRegex.test(inputValue)) {
    // Custom range with formatted string
    const [startStr, endStr] = inputValue.split(separatorRegex);
    start = moment(startStr, format);
    end = moment(endStr, format);
  } else if (inputValue.length === 10) {
    if (Number.isNaN(Number(inputValue))) {
      start = moment(inputValue, format);
      end = start;
    } else {
      const startUnix = Number(inputValue);
      start = moment.unix(startUnix);
      end = start;
    }
  } else {
    // Predefined ranges
    switch (inputValue) {
      case '1d':
        [start, end] = ranges['1 Day'];
        break;
      case '1w':
        [start, end] = ranges['1 Week'];
        break;
      case '2w':
        [start, end] = ranges['2 Weeks'];
        break;
      case '1M':
        [start, end] = ranges['1 Month'];
        break;
      default:
        break; // Invalid input, do nothing
    }
  }

  return { start, end };
}

const labelMatcher: { [key: string]: string } = {
  '1d': '1 Day',
  '1w': '1 Week',
  '2w': '2 Weeks',
  '1M': '1 Month',
}

const DateTimePicker: React.FC<DateTimePickerInputType> = ({
  id, style: stDraft, placeholder,
  value: valueDraft,
  tooltip,
  events: eventsDraft,
  soc,
  pageSettings,
  dataLake,
  active_state: activeStateDraft,
  regexp,
  settings,
  nestedTable,
  parentIndex,
  styleChunks,
  parentRef,
  skeleton: renderSkeleton2,
  parentLoading,
  parentStyle,
  enableLocalShimmer,
  valueOriginal,
  cache,
}) =>   {
  const launchExecutedRef = useRef<undefined | null | string>(null);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const inputRef = useMemo(() => {
    if (parentRef?.current) {
      return parentRef;
    }
    return React.createRef<HTMLInputElement>();
  }, [parentRef?.current]);
const { delay = 500, json, ref } = settings || {};
const transformedJSON = json ? transformKeysDeep(json) : {};
const store = useStore();
const { style: styleDraft, events, active_state, ref: realRef, value } = dataLake?.data ? replacePlaceholdersWithVariablesV2({ value: valueDraft, ref, style: stDraft, events: eventsDraft, active_state: activeStateDraft }, dataLake.data) : { value: valueDraft, ref: undefined, style: stDraft, events: eventsDraft, active_state: activeStateDraft };
const mergeStylesWithChunks = useCallback((style: any, chunks: any, index: number) => {
  const { indexes, refs } = chunks || {};
  let mergedStyle = style;
  
  // Apply index-based styles first
  if (indexes && indexes[id] && indexes[id][index] && typeof index === 'number') {
    mergedStyle = merge({}, mergedStyle, indexes[id][index]);
  }
  
  // Apply ref-based updates at the end
  if (refs && realRef) {
    const refUpdate = refs[realRef];
    if (refUpdate?.type && refUpdate.type === 'input') {
      mergedStyle = merge({}, mergedStyle, refUpdate?.update);
    }
  }
  
  return mergedStyle;
}, [id, realRef]);

  const renderSceleton = useMemo(() => {
    if (enableLocalShimmer) {
      if (cache === false) {
        return false;
      }
      return (active_state === 'loading' || parentLoading) ? !!((cache || (typeof valueOriginal === 'string' && /{{.*}}/.test(valueOriginal)))) : false;
    }
    return renderSkeleton2;
  }, [renderSkeleton2, active_state, enableLocalShimmer, valueOriginal, parentLoading]);

// First useMemo to merge active state styles with base style
const { effectiveState, entranceAnimation } = useInitEntrance(styleDraft, active_state, inputRef);
const mergedStyleWithState = useMemo(() => {
  let mergedStyle = styleDraft;
  if (effectiveState && styleDraft?.states?.[effectiveState]) {
    mergedStyle = mergeStyleWithState({}, styleDraft, styleDraft.states[effectiveState]);
  }

  // Handle states in breakpoints
  if (styleDraft?.breakpoints) {
    mergedStyle.breakpoints = Object.entries(styleDraft.breakpoints).reduce((acc, [breakpoint, breakpointStyle]) => {
      const typedBreakpointStyle = breakpointStyle as Breakpoints;
      if (typedBreakpointStyle?.states?.[effectiveState]) {
        acc[breakpoint] = mergeStyleWithState({}, typedBreakpointStyle, typedBreakpointStyle.states[effectiveState]);
      } else {
        acc[breakpoint] = typedBreakpointStyle;
      }
      return acc;
    }, {} as typeof styleDraft.breakpoints);
  }

  if (entranceAnimation) {
    mergedStyle = { ...mergedStyle, animation: entranceAnimation };
  }
  return mergedStyle;
}, [styleDraft, effectiveState, entranceAnimation]);

// Second useMemo to merge styles with chunks based on parentIndex
const style = useMemo(() => mergeStylesWithChunks(mergedStyleWithState, styleChunks, parentIndex), [mergedStyleWithState, styleChunks, parentIndex]);
const baseScreenResolution =  pageSettings?.baseScreenResolution ? getSizesBasedOnScreen(pageSettings?.baseScreenResolution) : undefined;
const { autofocus } = style || {};
const dispatch = useDispatch();

  // TNBEWEB-1992: shared handler scope (adds data.* / _c.* / _config.* / this.* on top of the
  // legacy $get/$set); `thisRow`/`rowPath` come from the repeatable row context (undefined otherwise).
  const internalFns = buildInternalFns({
    store,
    thisRow: dataLake?.data,
    rowPath: dataLake?.rowPath,
    owner: { id, parentIndex, row: dataLake?.data, activeState: active_state },
  });

  // Variables are assigned before every other chunk in the event (first in the
  // stack). Resolve them synchronously so the style/action/function chunks that
  // follow already see the new values. `{{this.*}}` resolves against this
  // component's own data.
  const applyVariablesFirst = (updates: any[]) => {
    const variableUpdates = (updates || []).filter((u) => isVariablesChunk(u));
    if (!variableUpdates.length) return;
    const state: any = store.getState();
    const data = buildVariablesData(variableUpdates as any, {
      variables: selectVariableData(state),
      localVariables: selectLocalVariableData(state),
      thisContext: dataLake?.data,
      formValues: state.page?.formValues,
    });
    if (data && Object.keys(data).length) {
      dispatch(updateVariableSuccess({ data }));
    }
  };

  const handleActionChunk = (actionUpdates: ActionChunk[], filterLoadingState?: StyleUpdateChunk[], onSuccessEvts?: any[], onErrorEvts?: any[]) => {
    actionUpdates.forEach((update) => {
      dispatch(actionRequest({ 
        actionUpdate: update, 
        componentId: id,
        onSuccess: onSuccessEvts,
        onError: onErrorEvts,
      }, filterLoadingState));
    });
  };

const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
const { 
  on_focus: focusStyles, 
  on_change: changeStyles, 
  on_blur: blurStyles, 
  on_launch: launchUpdates,
  on_hover: hoverUpdates,
  on_unhover: unhoverUpdates,
  on_regexp_success: regexpSuccessUpdates,
  on_regexp_error: regexpErorUpdates,
  on_outclick: outclickUpdates,
  on_success: onSuccessUpdates,
  on_error: onErrorUpdates,
} = events || {};

const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
  if (active_state === 'disabled' || active_state === 'loading') {
    e.preventDefault();
    e.stopPropagation();
    return;
  }
  const newValue = e.target.value;

      // Check max length
  if (regexp?.max_length && newValue.length > regexp.max_length) {
    return;
  }
  setInternalInputValue(newValue);
  dispatch(formValuesRequest({ field: { id:  realRef || id, value: newValue } }));
  // Check pattern validation
  if (regexp?.pattern && typeof regexp.pattern === 'string') {
    const result = !validateInputPattern(newValue, regexp.pattern);
    if (regexpSuccessUpdates && result) {
        const styleUpdates = regexpSuccessUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
        const requestUpdates = regexpSuccessUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
        const functionUpdates = regexpSuccessUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
        const actionUpdates = regexpSuccessUpdates.filter((update): update is ActionChunk => update && 'action' in update);
        applyVariablesFirst(regexpSuccessUpdates);
        const runEffects = () => {
          if (styleUpdates.length) {
            dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
          }
          if (actionUpdates.length) {
            const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
            handleActionChunk(actionUpdates, filterLoadingState, onSuccessUpdates, onErrorUpdates);
          }
          if (requestUpdates.length) {
            requestUpdates.forEach((update) => {
              const { method, value, silent, config } = update?.request || {};
              const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
              const { delay } = config || {};
              if (update?.request && method === 'command') {
                dispatch(pageRequest({
                  location: { pathname: value },
                  silent,
                  componentId: id,
                  onSuccess: onSuccessUpdates,
                  onError: onErrorUpdates,
                }, filterLoadingState));
              } else if (update?.request && method === 'action') {
                dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }, filterLoadingState));
              } else if (update?.request && method === 'open-url') {
                handleOpenUrl(value, delay, silent, history);
              }
            });
          }
        };
        runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    } else if (regexpErorUpdates && !result) {
        const styleUpdates = regexpErorUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
        const requestUpdates = regexpErorUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
        const functionUpdates = regexpErorUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
        const actionUpdates = regexpErorUpdates.filter((update): update is ActionChunk => update && 'action' in update);
        applyVariablesFirst(regexpErorUpdates);
        const runEffects = () => {
          if (styleUpdates.length) {
            dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
          }
          if (actionUpdates.length) {
            handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
          }
          if (requestUpdates.length) {
            requestUpdates.forEach((update) => {
              const { method, value, silent, config } = update?.request || {};
              const { delay } = config || {};
              if (update?.request && method === 'command') {
                dispatch(pageRequest({
                  location: { pathname: value },
                  silent,
                  componentId: id,
                  onSuccess: onSuccessUpdates,
                  onError: onErrorUpdates,
                }));
              } else if (update?.request && method === 'action') {
                dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
              } else if (update?.request && method === 'open-url') {
                handleOpenUrl(value, delay, silent, history);
              }
            });
          }
        };
        runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }
  }
  if (changeStyles) {
    const styleUpdates = changeStyles.filter((update): update is StyleUpdateChunk => update && 'style' in update);
    const requestUpdates = changeStyles.filter((update): update is RequestUpdateChunk => update && 'request' in update);
    const functionUpdates = changeStyles.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
    const actionUpdates = changeStyles.filter((update): update is ActionChunk => update && 'action' in update);
    applyVariablesFirst(changeStyles);
    const runEffects = () => {
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
        handleActionChunk(actionUpdates, filterLoadingState, onSuccessUpdates, onErrorUpdates);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
          const { delay } = config || {};
          if (update?.request && method === 'command') {
            dispatch(pageRequest({
              location: { pathname: value },
              silent,
              componentId: id,
              onSuccess: onSuccessUpdates,
              onError: onErrorUpdates,
            }, filterLoadingState));
          } else if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }, filterLoadingState));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    };
    runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
  }
  if (soc) {
    dispatch(submitFormRequest({ value: 'soc', delay, componentId: id }));
  }
};

const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
  setIsFocused(true);
  if (active_state === 'disabled' || active_state === 'loading') {
    e.preventDefault();
    e.stopPropagation();
    return;
  }
  if (focusStyles) {
    const styleUpdates = focusStyles.filter((update): update is StyleUpdateChunk => update && 'style' in update);
    const requestUpdates = focusStyles.filter((update): update is RequestUpdateChunk => update && 'request' in update);
    const functionUpdates = focusStyles.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
    const actionUpdates = focusStyles.filter((update): update is ActionChunk => update && 'action' in update);
    applyVariablesFirst(focusStyles);
    const runEffects = () => {
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'command') {
            dispatch(pageRequest({
              location: { pathname: value },
              silent,
              componentId: id,
              onSuccess: onSuccessUpdates,
              onError: onErrorUpdates,
            }));
          } else if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    };
    runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
  }
};

const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
  setIsFocused(false);
  if (active_state === 'disabled' || active_state === 'loading') {
    e.preventDefault();
    e.stopPropagation();
    return;
  }
  if (blurStyles) {
    const styleUpdates = blurStyles.filter((update): update is StyleUpdateChunk => update && 'style' in update);
    const requestUpdates = blurStyles.filter((update): update is RequestUpdateChunk => update && 'request' in update);
    const functionUpdates = blurStyles.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
    const actionUpdates = blurStyles.filter((update): update is ActionChunk => update && 'action' in update);
    applyVariablesFirst(blurStyles);
    const runEffects = () => {
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'command') {
            dispatch(pageRequest({
              location: { pathname: value },
              silent,
              componentId: id,
              onSuccess: onSuccessUpdates,
              onError: onErrorUpdates,
            }));
          } else if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    };
    runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
}
};

const handleHover = (e: React.PointerEvent<HTMLTextAreaElement>) => {
  if (e.pointerType === 'touch') return; // TNBEWEB-1980: finger tap — don't apply hover
  if (hoverUpdates) {
    const styleUpdates = hoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
    const requestUpdates = hoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
    const functionUpdates = hoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
    const actionUpdates = hoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
    applyVariablesFirst(hoverUpdates);
    const runEffects = () => {
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
        handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
      }
      if (requestUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    };
    runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);

  }

}

const handleUnhover = (e:  React.PointerEvent<HTMLTextAreaElement>) => {
  if (e.pointerType === 'touch') return; // TNBEWEB-1980: finger tap — don't apply unhover
  if (unhoverUpdates) {
    const styleUpdates = unhoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
    const requestUpdates = unhoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
    const functionUpdates = unhoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
    const actionUpdates = unhoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
    applyVariablesFirst(unhoverUpdates);
    const runEffects = () => {
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
        handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
      }
      if (requestUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    };
    runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
  }
}

useEffect(() => {
  if (launchExecutedRef.current !== style?.visibility && launchUpdates && launchUpdates.length) {
    launchExecutedRef.current = style?.visibility; // Set the flag to true
    const requestUpdates = launchUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
    const functionUpdates = launchUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
    const actionUpdates = launchUpdates.filter((update): update is ActionChunk => update && 'action' in update);
    applyVariablesFirst(launchUpdates);
    const runEffects = () => {
      if (actionUpdates.length) {
        handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            const newTimeout = setTimeout(() => {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
            }, config?.delay * 1000 || 0);

            timeoutsRef.current.push(newTimeout);
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    };
    runFunctionsWithTiming(functionUpdates, internalFns, runEffects);
  }

  return () => {
    timeoutsRef?.current?.forEach((timeout) => {
      clearTimeout(timeout);
    });
  };
}, [launchUpdates, style?.visibility]);

  // Memoize the filtered updates to avoid recalculating on every click
  const filteredUpdates = useMemo(() => {
    if (!outclickUpdates) return null;
    
    return {
      styleUpdates: outclickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update),
      requestUpdates: outclickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update),
      functionUpdates: outclickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update),
      actionUpdates: outclickUpdates.filter((update): update is ActionChunk => update && 'action' in update)
    };
  }, [outclickUpdates]);

  useEffect(() => {
    if (!outclickUpdates?.length) return undefined;
    if (style?.visibility === 'hidden') return undefined;

    const handleUnclick = (e: MouseEvent) => {
      if (active_state === 'disabled' || active_state === 'loading') return;
      
      if (
        outclickUpdates?.length &&
        inputRef.current && 
        !inputRef.current.contains(e.target as Node) &&
        !isClickInsidePortal(e.target as Node) &&
        filteredUpdates
      ) {
        const runEffects = () => {
          // Apply style updates
          if (filteredUpdates.styleUpdates.length) {
            dispatch(mergeStyleRequest(filteredUpdates.styleUpdates, parentIndex));
          }

          // Handle action updates
          if (filteredUpdates.actionUpdates.length) {
            handleActionChunk(filteredUpdates.actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
          }

          // Process request updates
          if (filteredUpdates.requestUpdates.length) {
            filteredUpdates.requestUpdates.forEach((update) => {
              const { method, value, silent, config } = update?.request || {};
              const { delay } = config || {};
              if (update?.request && method === 'action') {
                dispatch(submitFormRequest({
                  value,
                  silent,
                  delay,
                  componentId: id,
                  onSuccess: onSuccessUpdates,
                  onError: onErrorUpdates,
                }));
              } else if (update?.request && method === 'open-url') {
                handleOpenUrl(value, delay, silent, history);
              }
            });
          }
        };
        applyVariablesFirst(outclickUpdates);
        runFunctionsWithTiming(filteredUpdates.functionUpdates, internalFns, runEffects);
      }
    };
    
    document.addEventListener('mousedown', handleUnclick, true);
    return () => {
      document.removeEventListener('mousedown', handleUnclick, true);
    };
  }, [
    parentIndex, 
    active_state, 
    id, 
    stDraft?.states?.loading,
    outclickUpdates?.length,
    style?.visibility,
  ]);

const prevSelectionRef = useRef({});

const [internalInputValue, setInternalInputValue] = useState<string>('');
const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);

useEffect(() => {
  if (value) {
    const locale = transformedJSON?.locale || {};
    const { separator = ' : ', format = 'HH:mm DD/MM/YY' } = locale || {};
    const { start, end } = getTimeRanges(value, {}, separator, format);
    const separatorRegex = new RegExp(`\\s*${separator.trim()}\\s*`);

      // Always update internal value when external value changes
      if (separatorRegex.test(value)) {
        setInternalInputValue(`${start?.format(format)} - ${end?.format(format)}`);
      } else if (value.length === 10) {
        setInternalInputValue(start?.format(format) || '');
      } else {
        setInternalInputValue(labelMatcher[value]);
      }
    } else {
      // Clear internal value when external value is cleared
      setInternalInputValue('');
      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.dates = [];
      }
    }
}, [value]);

useEffect(() => {
  if (!isFocused) return;
  const dateRangePickerContainer = document.createElement('div');
  dateRangePickerContainer.id = `dateRangePickerContainer-${id}`;
  document.body.appendChild(dateRangePickerContainer);
  const { start: startUnix, end: endUnix } = getTimeRanges(value, {}, transformedJSON?.locale?.separator, transformedJSON?.locale?.format);
    // Use refs to store the previous selection for comparison
  prevSelectionRef.current = {
      startStr: startUnix ? startUnix.format(transformedJSON?.locale?.format || 'DD/MM/YYYY') : '',
      endStr: endUnix ? endUnix.format(transformedJSON?.locale?.format || 'DD/MM/YYYY') : ''
    };
  // No value yet: the plugin would default the calendar to TODAY and only clamps
  // that default against minDate (not maxDate), so for a past maxDate the year
  // dropdown shows its first option (minDate's year, e.g. 1930). Open at maxDate
  // instead (clamped up to minDate) so the initial year is inside the range.
  // Parse the bounds with the locale format ("05.06.2008" = 5 June in DD.MM.YYYY).
  const localeFormat = transformedJSON?.locale?.format || 'DD/MM/YYYY';
  const parsedMin = transformedJSON?.minDate ? moment(transformedJSON.minDate, localeFormat) : undefined;
  const parsedMax = transformedJSON?.maxDate ? moment(transformedJSON.maxDate, localeFormat) : undefined;
  let initialStart = startUnix?.isValid() ? startUnix : undefined;
  if (!initialStart && parsedMax?.isValid()) {
    initialStart = parsedMax.clone();
    if (parsedMin?.isValid() && initialStart.isBefore(parsedMin)) initialStart = parsedMin.clone();
  }
  const $dateRangePicker = $(inputRef.current).daterangepicker({
    startDate: initialStart,
    endDate: endUnix?.isValid() ? endUnix : initialStart,
    ranges: typeof (transformedJSON?.ranges) === "undefined" ? ranges(transformedJSON?.minDate, transformedJSON?.maxDate, transformedJSON?.maxSpan) : transformedJSON.ranges,
    cancelButtonClasses: styles.cancelBtn,
    showCustomRangeLabel: false,
    applyButtonClasses: styles.applyBtn,
    timePicker: true,
    timePicker24Hour: true,
    timePickerSeconds: false,
    alwaysShowCalendars: true,
    opens: 'left',
    autoUpdateInput: false,
    maxDate: moment().endOf('day'),
    locale: {
      format: 'DD/MM/YYYY',
      separator: ' : ',
      applyLabel: 'Save',
      cancelLabel: 'Cancel',
      fromLabel: 'From',
      toLabel: 'To',
      customRangeLabel: 'Custom',
      weekLabel: 'W',
      daysOfWeek: ['Sub', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
      monthNames: [
        'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'
      ],
      firstDay: 1
    },
    ...(transformedJSON || {}),
  }, (start: Moment, end: Moment, label: string) => {
    if (label === '1 Day' || label === '1 Week' || label === '2 Weeks' || label === '1 Month') {
      setInternalInputValue(label);
    } else if (transformedJSON?.singleDatePicker) {
      setInternalInputValue(start.format(transformedJSON.locale?.format));
    } else {
      setInternalInputValue(`${start.format(transformedJSON.locale?.format)} - ${end.format(transformedJSON.locale?.format)}`);
    }

        // Create and dispatch a synthetic input event
        const syntheticEvent = {
          target: {
            value: label === '1 Day' || label === '1 Week' || label === '2 Weeks' || label === '1 Month' 
              ? label 
              : transformedJSON?.singleDatePicker 
                ? start.format(transformedJSON.locale?.format)
                : `${start.format(transformedJSON.locale?.format)} - ${end.format(transformedJSON.locale?.format)}`
          },
          preventDefault: () => {},
          stopPropagation: () => {}
        } as React.ChangeEvent<HTMLTextAreaElement>;
    
        // Call handleInput with the synthetic event
        handleInput(syntheticEvent);
  });

// // Add an event listener for the "apply.daterangepicker" event
$dateRangePicker.on('apply.daterangepicker', (ev, picker) => {
  // Get the current selection
  const currentStartStr = picker.startDate.format(transformedJSON?.locale?.format || 'DD/MM/YYYY');
  const currentEndStr = picker.endDate.format(transformedJSON?.locale?.format || 'DD/MM/YYYY');
  
  if (picker.chosenLabel === '1 Day' || picker.chosenLabel === '1 Week' || picker.chosenLabel === '2 Weeks' || picker.chosenLabel === '1 Month') {
    setInternalInputValue(picker.chosenLabel);
  } else if (transformedJSON?.singleDatePicker) {
    setInternalInputValue(picker.startDate.format(transformedJSON.locale?.format));
  } else {
    setInternalInputValue(`${picker.startDate.format(transformedJSON.locale?.format)} - ${picker.endDate.format(transformedJSON.locale?.format)}`);
  }
  
  // Create and dispatch a synthetic input event
  const syntheticEvent = {
    target: {
      value: picker.chosenLabel === '1 Day' || picker.chosenLabel === '1 Week' || picker.chosenLabel === '2 Weeks' || picker.chosenLabel === '1 Month' 
        ? picker.chosenLabel 
        : transformedJSON?.singleDatePicker 
          ? picker.startDate.format(transformedJSON.locale?.format)
          : `${picker.startDate.format(transformedJSON.locale?.format)} - ${picker.endDate.format(transformedJSON.locale?.format)}`
    },
    preventDefault: () => {},
    stopPropagation: () => {}
  } as React.ChangeEvent<HTMLTextAreaElement>;
  
  // Call handleInput with the synthetic event
  handleInput(syntheticEvent);
  
  // Update the previous selection reference
  prevSelectionRef.current = {
    startStr: currentStartStr,
    endStr: currentEndStr
  };
});

  $dateRangePicker.on('show.daterangepicker', () => {
    setIsCalendarOpen(true);

    const $containers = $('.daterangepicker');
    const $visibleContainer = $containers.filter(function () {
      return $(this).css('display') !== 'none';
    }).first();

    if ($visibleContainer?.length) {
      applyDrpColors($visibleContainer[0] as HTMLElement, transformedJSON?.colors);

      // Apply zoom based on the coefficient
      const zoom = transformedJSON?.style?.zoom || 1;
      $visibleContainer.children().css('zoom', zoom);

      // Calculate total width based on all .drp-calendar elements
      const $calendars = $visibleContainer.find('.drp-calendar');
      const $ranges = $visibleContainer.find('.ranges');
      let totalWidth = 0;
      
      $calendars.each(function() {
        // Only include elements that are not hidden
        if ($(this).css('display') !== 'none') {
          totalWidth += $(this).outerWidth(true) || 0;
        }
      });

      $ranges.each(function() {
        // Only include elements that are not hidden
        if ($(this).css('display') !== 'none') {
          totalWidth += $(this).outerWidth(true) || 0;
        }
      });
      
      // Apply zoom factor to the width calculation
      if (zoom && zoom !== 1) {
        totalWidth *= zoom;
      }
      
      // Add padding/margin to ensure everything fits
      if (totalWidth > 0) {
        // Add some extra space for padding, borders, etc.
        totalWidth += 10;
        $visibleContainer.css('width', `${totalWidth}px`);
      }

      const offset = $visibleContainer.offset();
      if (offset) {
        // Only adjust position if it's outside the viewport
        const rect = $visibleContainer[0].getBoundingClientRect();

        // Check if calendar extends beyond viewport edges
        const windowHeight = window.innerHeight;
        const hasTopOverflow = rect.top < 0;
        const hasLeftOverflow = rect.left < 0;
        const hasBottomOverflow = rect.bottom > windowHeight;

        if (rect && (hasTopOverflow || hasLeftOverflow || hasBottomOverflow)) {
          let newTop = offset.top;
          if (hasTopOverflow) {
            newTop = 0;
          } else if (hasBottomOverflow) {
            // If overflow at bottom, move the calendar up just enough to fit in viewport
            const bottomOverflow = rect.bottom - windowHeight;
            newTop = offset.top - bottomOverflow;
            // Ensure we don't create a new top overflow
            newTop = Math.max(0, newTop);
          }
          
          const newLeft = hasLeftOverflow ? 0 : offset.left;

          // Generate a dynamic class with important styles
          const dynamicClassName = 'daterangepicker-adjusted-position';
          const styleElementId = 'daterangepicker-dynamic-styles';

          // Check if the style element already exists
          let styleElement = document.getElementById(styleElementId) as HTMLStyleElement;
          if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = styleElementId;
            document.head.appendChild(styleElement);
          }

          // Add the dynamic styles
          styleElement.innerHTML = `
            .${dynamicClassName} {
              top: ${newTop}px !important;
              left: ${newLeft}px !important;
              position: absolute !important;
              width: ${totalWidth}px !important;
            }
          `;

          // Apply the class to the visible container
          $visibleContainer.addClass(dynamicClassName);
        }
      }
    }

    const drpSelectedNode = document.querySelector('.drp-selected');
    if (drpSelectedNode && (transformedJSON?.singleDatePicker)) {
      (drpSelectedNode as HTMLElement).style.display = 'none';
    } else if (drpSelectedNode && (typeof transformedJSON?.ranges !== 'undefined')) {
      (drpSelectedNode as HTMLElement).style.left = '34px';
      (drpSelectedNode as HTMLElement).style.display = 'inline';
    } else if (drpSelectedNode) {
      (drpSelectedNode as HTMLElement).style.left = '226px';
      (drpSelectedNode as HTMLElement).style.display = 'inline';
    }
  });

  $dateRangePicker.on('hide.daterangepicker', () => {
    setIsCalendarOpen(false);
  });

  $dateRangePicker.data('daterangepicker').show();

  // Cleanup event listeners on component unmount
  return () => {
    $dateRangePicker.off('show.daterangepicker');
    $dateRangePicker.off('hide.daterangepicker');

    document.body.removeChild(dateRangePickerContainer);
  };
}, [value, id, isFocused]);

if (renderSceleton && style) {
  return <ContainerSkeleton 
    style={style}
    baseScreenResolution={baseScreenResolution}
    skipStates={active_state === 'disabled' || active_state === 'loading'}
  />;
}

return (
  <StyledInput
    ref={inputRef}
    style={{ display: style?.visibility === 'hidden' ? 'none' : undefined }}
    placeholder={placeholder}
     data-id={realRef || id}
    id={`${realRef || id}${(nestedTable && typeof parentIndex === 'number' && !realRef) ? `::${parentIndex}` : ''}`}
    type="text"
    $shape={style}
    $baseScreenResolution={baseScreenResolution}
    $skipStates={active_state === 'disabled' || active_state === 'loading'}
    $parentDisplay={parentStyle?.display}
    $parentFlexDirection={typeof parentStyle?.flex === 'object' ? parentStyle?.flex?.direction : undefined}
    disabled={active_state === 'disabled' || active_state === 'loading'}
    data-component="input"
    className={combineClassNames(style.class)}
    value={internalInputValue}
    title={tooltip}
    autoComplete="off"
    autoFocus={autofocus}
    onInput={handleInput}
    onFocus={handleFocus}
    onBlur={handleBlur}
      onPointerEnter={hoverUpdates?.length ? handleHover : undefined}
      onPointerLeave={unhoverUpdates?.length ? handleUnhover : undefined}
    maxLength={regexp?.max_length}
  />
);
};

export default DateTimePicker;